import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  LinearProgress,
  Chip,
  Stack,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material'
import {
  Psychology as AIIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RetryIcon
} from '@mui/icons-material'
import { ProcessingSession, ExtractedQuestion, ProgressInfo } from '../../types'
import { config } from '../../config/environment'
import { getQuestionTypeLabel } from '../../utils/questionTypes'

interface QuestionExtractionStepProps {
  session: ProcessingSession
  onExtractionComplete?: (questions: ExtractedQuestion[]) => void
  onError?: (error: string) => void
  onProgressUpdate?: (progress: ProgressInfo) => void
}

export const QuestionExtractionStep: React.FC<QuestionExtractionStepProps> = ({
  session,
  onExtractionComplete,
  onError,
  onProgressUpdate
}) => {
  const [isExtracting, setIsExtracting] = useState(false)
  const [progress, setProgress] = useState({
    phase: 'ready',
    current: 0,
    total: 100,
    message: 'Ready to extract questions',
    percentage: 0
  })
  const [extractedQuestions, setExtractedQuestions] = useState<ExtractedQuestion[]>([])
  const [extractionComplete, setExtractionComplete] = useState(false)

  // Poll for progress updates during extraction
  useEffect(() => {
    let pollInterval: NodeJS.Timeout

    if (isExtracting) {
      pollInterval = setInterval(async () => {
        try {
          const response = await fetch(`${config.API_BASE_URL}/api/tabplan/status/${session.sessionId}`, { credentials: 'include' })
          if (response.ok) {
            const result = await response.json()
            if (result.success && result.session) {
              const newProgress = {
                phase: result.session.progress?.phase || 'processing',
                current: result.session.progress?.current || 0,
                total: result.session.progress?.total || 100,
                message: result.session.progress?.message || 'Processing...',
                percentage: result.session.progress?.current && result.session.progress?.total 
                  ? Math.round((result.session.progress.current / result.session.progress.total) * 100)
                  : 0
              }
              setProgress(newProgress)
              
              // Update parent component with progress
              onProgressUpdate?.({
                type: newProgress.phase,
                percentage: newProgress.percentage,
                current: newProgress.current,
                total: newProgress.total,
                message: newProgress.message
              })

              // Check if extraction is complete
              if (result.session.status === 'ready_for_review') {
                setIsExtracting(false)
                await fetchExtractedQuestions()
              } else if (result.session.status === 'error') {
                setIsExtracting(false)
                onError?.('Question extraction failed. Please try again.')
              }
            }
          }
        } catch (error) {
          console.error('Error polling status:', error)
        }
      }, 2000)
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }
  }, [isExtracting, session.sessionId, onError])

  const fetchExtractedQuestions = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/tabplan/questions/${session.sessionId}`, { credentials: 'include' })
      if (response.ok) {
        const result = await response.json()
        if (result.success && result.questions) {
          setExtractedQuestions(result.questions)
          setExtractionComplete(true)
          onExtractionComplete?.(result.questions)
        }
      }
    } catch (error) {
      console.error('Error fetching questions:', error)
      onError?.('Failed to retrieve extracted questions')
    }
  }

  const startExtraction = async () => {
    setIsExtracting(true)
    setProgress({
      phase: 'starting',
      current: 0,
      total: 100,
      message: 'Starting question extraction...',
      percentage: 0
    })

    try {
      const response = await fetch(`${config.API_BASE_URL}/api/tabplan/extract`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: session.sessionId,
          batchSize: 12,
          concurrent: true
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Extraction failed')
      }

      const result = await response.json()
      
      if (!result.success) {
        throw new Error(result.message || 'Extraction failed')
      }

      // Progress polling will handle the rest
    } catch (error) {
      console.error('Extraction error:', error)
      setIsExtracting(false)
      onError?.(error instanceof Error ? error.message : 'Extraction failed')
    }
  }

  const getPhaseDescription = (phase: string) => {
    switch (phase) {
      case 'extraction_phase1':
        return 'Identifying question patterns in document...'
      case 'extraction_phase2':
        return 'Extracting detailed question information...'
      case 'processing':
        return 'Processing questions with AI...'
      case 'complete':
        return 'Question extraction completed!'
      default:
        return 'Processing...'
    }
  }

  const getQuestionTypeColor = (type: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'error'> = {
      'single_choice': 'primary',
      'multi_choice': 'secondary',
      'open_end_text': 'success',
      'open_end_video': 'warning',
      'numeric': 'primary',
      'text_display': 'secondary',
      'grid': 'error'
    }
    return colors[type] || 'secondary'
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Extract Survey Questions
      </Typography>
      
      <Typography color="text.secondary" paragraph>
        Use AI to automatically extract and analyze survey questions from your uploaded document.
        This process identifies question types, answer options, and programming logic.
      </Typography>

      {!isExtracting && !extractionComplete && (
        <Paper elevation={1} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <AIIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography variant="h6">
              Ready to Extract Questions
            </Typography>
            <Typography color="text.secondary">
              Click the button below to start AI-powered question extraction from your document.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={startExtraction}
              startIcon={<AIIcon />}
            >
              Start Question Extraction
            </Button>
          </Stack>
        </Paper>
      )}

      {isExtracting && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="h6">
                Extracting Questions...
              </Typography>
            </Box>
            
            <Typography color="text.secondary">
              {getPhaseDescription(progress.phase)}
            </Typography>
            
            <LinearProgress 
              variant="determinate" 
              value={progress.percentage} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            
            <Typography variant="body2" color="text.secondary">
              {progress.message}
            </Typography>

            {progress.percentage > 0 && (
              <Typography variant="body2" color="text.secondary">
                Progress: {progress.current} of {progress.total} ({progress.percentage}%)
              </Typography>
            )}
          </Stack>
        </Paper>
      )}

      {extractionComplete && extractedQuestions.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckIcon sx={{ color: 'success.main' }} />
              <Typography variant="h6">
                Extraction Complete!
              </Typography>
            </Box>
            
            <Typography color="text.secondary">
              Successfully extracted {extractedQuestions.length} questions from your document.
            </Typography>

            {/* Question Summary */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Question Types Found:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Object.entries(
                  extractedQuestions.reduce((acc, q) => {
                    acc[q.question_type] = (acc[q.question_type] || 0) + 1
                    return acc
                  }, {} as Record<string, number>)
                ).map(([type, count]) => (
                  <Chip
                    key={type}
                    label={`${getQuestionTypeLabel(type)}: ${count}`}
                    size="small"
                    color={getQuestionTypeColor(type)}
                  />
                ))}
              </Stack>
            </Box>

            {/* Sample Questions Preview */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Sample Questions:
              </Typography>
              <Stack spacing={1}>
                {extractedQuestions.slice(0, 3).map((question, index) => (
                  <Card key={question.question_id} variant="outlined" sx={{ p: 1 }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="body2" fontWeight="medium">
                        {question.question_id}: {question.question_text}
                      </Typography>
                      <Chip 
                        label={getQuestionTypeLabel(question.question_type)} 
                        size="small" 
                        color={getQuestionTypeColor(question.question_type)}
                        sx={{ mt: 0.5 }}
                      />
                    </CardContent>
                  </Card>
                ))}
                {extractedQuestions.length > 3 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    ... and {extractedQuestions.length - 3} more questions
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </Paper>
      )}

      {extractionComplete && extractedQuestions.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            No questions were found in your document. Please check that your document contains 
            properly formatted survey questions and try uploading a different file.
          </Typography>
        </Alert>
      )}

      <Alert severity="info">
        <Typography variant="body2">
          <strong>AI Processing:</strong> This step uses advanced AI to understand your survey structure 
          and extract question details. The process typically takes 1-3 minutes depending on document size.
        </Typography>
      </Alert>
    </Box>
  )
}

export default QuestionExtractionStep